import { LmViewer3D } from "@/types/LmViewer3D/LmViewer3D";
import { ModelTreeNode } from "@/types/ModelTreeNode";
import { ProgressIndicator } from "@/types/ProgressIndicator";
import { SelectionManager } from "@/types/SelectionManager";
import { ViewerToken } from "@/types/ViewerToken";
import { ErrorManager } from "@/types/errors/ErrorManager";
import { SankeyOptionKeys, SankeyOptions } from "@/types/sankeyOptions";
import { ApplicationSettings, FeatureManagementType, Model, Project, Scenario, UserProfile } from "@api/apiClient";
import { Theme } from "@mui/material";
import JobStatusStore from "@stores/JobStatusStore";
import { QueryCache, QueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { makeAutoObservable, toJS } from "mobx";
import { EtlJobStatusStore } from "./EtlJobStatusStore";
// import { jwtDecode } from 'jwt-decode';

// Add to the global window object for auto-completion in the console
// > window.AppStore
export { };
declare global {
  interface Window {
    AppStore: AppStore;
  }
}

class AppStore {
  public persistSettings = ["isModelEnabled", "colorMode", "language"];
  public isAuthenticated: boolean = false;
  public isInitialized: boolean = false;
  public isInitializing: boolean = false;
  public isInitializingCount: number = 0;
  public isModelEnabled: boolean = true;
  public isProjectStepperEnabled: boolean = true;
  public isLoggingEnabled: boolean = false;
  public isProfilingEnabled: boolean = false;
  public language: string = "en";
  public isPending: boolean = false;
  public isPendingCount: number = 0;
  public userProfile: UserProfile | null = null;
  public applicationSettings: ApplicationSettings | null = null;
  /*
   * Settings Drawer State
   */
  public openSettings: boolean = false;
  /*
   * Theme State
   */
  public colorMode: "light" | "dark" = "light";
  public defaultTheme!: Theme;
  /*
   * Global Error Manager
   */
  public errorManager: ErrorManager = new ErrorManager();
  /*
   * React Query global state
   */
  public queryClient: QueryClient;
  public queryCache: QueryCache;
  /*
   * Header - Progress Indicator
   */
  public progressIndicator: ProgressIndicator = new ProgressIndicator();
  /*
   * Sustainability SDA sync - persistent in-progress state (no auto-hide)
   */
  public sustainabilitySyncInProgress: boolean = false;
  public sustainabilitySyncMessage: string = "";
  /*
   * Results tab setting state
   */
  public openResultsSettings: boolean = false;
  public showGrid: boolean = true;
  public showChart: boolean = true;
  public showSankey: boolean = false;
  public sankeyOptions: SankeyOptions = {
    showScenarios: true,
    showModels: true,
    showCategories: true,
    showCadMaterials: true,
    showCo2MaterialTypes: true,
    showSystems: true,
    showSubSystems: true,
    showAttributes: true,
    showEmptyNodes: true,
    showLayerTitles: true,
  };

  /*
   * Currently Selected Project, Scenario and Model
   */
  public project: Project | null = null;
  public scenario: Scenario | null = null;
  public model: Model | null = null;
  /*
   * Assign Grid state
   */
  public assignNodes: ModelTreeNode[] | null = [];
  public: ModelTreeNode[] | null = [];
  public assignNodesRecalc: number = 0;
  public expandedKeys: any = {};
  public selectedNodesKeys: any = {};
  public selectedModelCategories: Record<string, ModelTreeNode> = {};
  /*
   * Assign Grid temporary state for rebar edits
   */
  public rebarPercentEdits: Record<string, any> = {};
  public rebarVolumeEdits: Record<string, any> = {};
  public rebarCO2eEdits: Record<string, any> = {};
  /*
   * Assign Grid temporary state for non-modelled edits
   */
  public nonModelledPercentEdits: Record<string, any> = {};
  public nonModelledVolumeEdits: Record<string, any> = {};
  public nonModelledCO2eEdits: Record<string, any> = {};
  /*
   * MaterialSelectDialog selected material libraries
   */
  public co2MaterialLibraryIds: string[] = [];
  /*
   * 3D Viewers and Selection State
   */
  public selectionManager: SelectionManager = new SelectionManager();
  public lmViewer1: LmViewer3D | undefined;
  public lmViewer2: LmViewer3D | undefined;
  /*
   * Design Automation and Model Derivative Jobs Status
   */
  public jobStatus: JobStatusStore = new JobStatusStore();
  public etlJobStatus: EtlJobStatusStore = new EtlJobStatusStore();
  /*
   * Backup of console command for enable/disable logging
   */
  public consoleLog: any;
  /*
   * Viewer Token
   */
  public viewerToken: ViewerToken = new ViewerToken();
  /*
   * Project Tab Bar Splitter Sizes
   */
  public projectTabBarSizes: number[] = [50.0, 50.0];
  /*
   * Material Select Dialog Splitter Sizes
   */
  public materialSelectDialogSizes: number[] = [50.0, 50.0];
  /*
   * Scenario Grid refresh clone
   */
  public scenarioName: string = '';
  /*
   * Model refresh copy material
   */
  public modelName: string = '';

  constructor(queryClient: QueryClient, queryCache: QueryCache) {
    window.AppStore = this;
    makeAutoObservable(
      this,
      {
        defaultTheme: false,

        assignNodes: false,
        expandedKeys: false,
        selectedNodesKeys: true,
        selectedModelCategories: false,

        viewer1: false,
        viewer2: false,
        lmViewer1: false,
        lmViewer2: false,
      },
      {
        autoBind: true,
      },
    );

    this.queryClient = queryClient;
    this.queryCache = queryCache;

    // TODO: change this to use mitt events
    this.selectionManager.onLoad = this.onLoad.bind(this);
    this.selectionManager.onUnload = this.onUnload.bind(this);

    //this.selectionManager.emitter.on(SelectionManagerEvents.SELECTION_ONLOAD_EVENT, this.onLoad.bind(this));
    //this.selectionManager.emitter.on(SelectionManagerEvents.SELECTION_ONUNLOAD_EVENT, this.onUnload.bind(this));
  }

  get dump() {
    return toJS(this);
  }

  /**
   * Get the APS viewer for the LMV Viewer1 3D viewer.
   * Use .lmViewer1 to access the LmViewer3D wrapper for viewer1.
   * 
   * @returns {Autodesk.Viewing.GuiViewer3D | undefined}
   */
  get viewer1(): Autodesk.Viewing.GuiViewer3D | undefined {
    return this.lmViewer1?.viewer;
  }

  /**
   * Get the APS viewer for the LMV Viewer2 3D viewer.
   * Use .lmViewer2 to access the LmViewer3D wrapper for viewer2.
   * 
   * @returns {Autodesk.Viewing.GuiViewer3D | undefined}
   */
  get viewer2(): Autodesk.Viewing.GuiViewer3D | undefined {
    return this.lmViewer2?.viewer;
  }

  get settings() {
    return this.applicationSettings?.settings || {};
  }

  get features() {
    return this.applicationSettings?.features || [];
  }

  get metadataEntities() {
    return this.applicationSettings?.metadataEntities || [];
  }
  
  get hasRebarPercentEdits(): boolean {
    return Object.keys(this.rebarPercentEdits).length > 0;
  }

  get hasNonModelledPercentEdits(): boolean {
    return Object.keys(this.nonModelledPercentEdits).length > 0;
  }

  hasFeature = (feature: FeatureManagementType): boolean => {
    return this.features.includes(feature);
  };

  // TODO: change these to use mitt events
  onLoad = async (scenarioId: string, viewerId: string, urns: string | string[]) => {
    console.log(`AppStore:onLoad(${scenarioId}, ${viewerId}, ${urns})`);

    if (this.isModelEnabled === false) {
      return;
    }

    if (viewerId === "viewer1") {
      const urnArray = Array.isArray(urns) ? urns : [urns];
      for (const urn of urnArray) {
        try {
          await this.lmViewer1?.loadDocument(urn);
        } catch (error) {
          console.log("error: ", error);
        }
      }
    }
    if (viewerId === "viewer2") {
      const urnArray = Array.isArray(urns) ? urns : [urns];
      for (const urn of urnArray) {
        try {
          await this.lmViewer2?.loadDocument(urn);
        } catch (error) {
          console.log("error: ", error);
        }
      }
    }
  };

  // TODO: change these to use mitt events
  onUnload = async (scenarioId: string, viewerId: string, urns: string | string[]) => {
    console.log(`AppStore:onUnload(${scenarioId}, ${viewerId}, ${urns})`);

    if (this.isModelEnabled === false) {
      return;
    }

    // TODO: change unloadDocument to unloadDocuments(urns: string[]) to cleanup this code
    if (viewerId === "viewer1") {
      const urnArray = Array.isArray(urns) ? urns : [urns];
      for (const urn of urnArray) {
        try {
          await this.lmViewer1?.unloadDocument(urn);
        } catch (error) {
          console.log("error: ", error);
        }
      }
    }
    if (viewerId === "viewer2") {
      const urnArray = Array.isArray(urns) ? urns : [urns];
      for (const urn of urnArray) {
        try {
          await this.lmViewer2?.unloadDocument(urn);
        } catch (error) {
          console.log("error: ", error);
        }
      }
    }
  };

  setDefaults = () => {
    this.setColorMode(localStorage.getItem("colorMode") === "dark" ? "dark" : "light");
    this.setIsModelEnabled(localStorage.getItem("isModelEnabled") !== "false");
    this.setIsProjectStepperEnabled(localStorage.getItem("isProjectStepperEnabled") !== "false");
    this.setIsLoggingEnabled(localStorage.getItem("isLoggingEnabled") === "true");
    this.setIsProfilingEnabled(localStorage.getItem("isProfilingEnabled") === "true");
    this.setMaterialLibraryIds(localStorage.getItem("co2MaterialLibraryIds")?.split(",") || []);
    const sizes1 = JSON.parse(localStorage.getItem("projectTabBarSizes") || "[50.0, 50.0]");
    this.setProjectTabBarSizes(sizes1);
    const sizes2 = JSON.parse(localStorage.getItem("materialSelectDialogSizes") || "[50.0, 50.0]");
    this.setMaterialSelectDialogSizes(sizes2);

    this.setShowGrid(localStorage.getItem("showResultsGrid") !== "false");
    this.setShowChart(localStorage.getItem("showResultsChart") !== "false");
    this.setShowSankey(localStorage.getItem("showResultsSankey") === "true");
    const sankeyOptions = JSON.parse(localStorage.getItem("sankeyOptions") || "{}");
    this.setSankeyOptions(sankeyOptions);
  };

  setUserProfile = (userProfile: UserProfile | null) => {
    this.userProfile = userProfile;
  };

  setOpenSettings = (openSettings: boolean) => {
    this.openSettings = openSettings;
  };

  toggleOpenSettings = () => {
    this.openSettings = !this.openSettings;
  };

  setSustainabilitySyncInProgress = (message: string | null) => {
    this.sustainabilitySyncInProgress = message !== null;
    this.sustainabilitySyncMessage = message ?? "";
  };

  setIsModelEnabled = (isModelEnabled: boolean) => {
    this.isModelEnabled = isModelEnabled;
    localStorage.setItem("isModelEnabled", this.isModelEnabled.toString());
  };

  toggleIsModelEnabled = () => {
    this.isModelEnabled = !this.isModelEnabled;
    localStorage.setItem("isModelEnabled", this.isModelEnabled.toString());
  };

  setAuthenticated = (isAuthenticated: boolean) => {
    this.isAuthenticated = isAuthenticated;
  };

  setInitialized = (isInitialized: boolean) => {
    this.isInitialized = isInitialized;
  };

  setIsInitializing = (isLoading: boolean) => {
    this.isInitializingCount = Math.max(0, this.isInitializingCount + (isLoading ? 1 : -1));
    this.isInitializing = this.isInitializingCount > 0;
  };

  setLanguage = (language: string) => {
    //console.log("setLanguage: ", language);
    this.language = language;
  };

  setProject = (project: Project) => {
    //console.log("setProject: ", project);
    this.project = project;
  };

  // setProjectPermissions = (permissions: string[]) => {
  //   this.projectPermissions = permissions;
  // };

  setAppSettings = (appSettings: ApplicationSettings) => {
    this.applicationSettings = appSettings;
  };

  setScenario = (scenario: Scenario | null) => {
    this.scenario = scenario;
  };

  setMaterialLibraryIds = (materialLibraryIds: string[]) => {
    this.co2MaterialLibraryIds = materialLibraryIds;
    localStorage.setItem("co2MaterialLibraryIds", this.co2MaterialLibraryIds.join(","));
  };

  setAssignNodes = (assignNodes: ModelTreeNode[] | null) => {
    this.assignNodes = assignNodes;
    this.assignNodesRecalc++;
    //console.log("appStore.assignNodes: ", toJS(this.assignNodes));
  };

  setModel = (model: Model | null) => {
    this.model = model;

    this.resetRebarModelEdits();
    this.resetNonModelledModelEdits();
  };

  setDefaultModel = () => {
    if (this.project?.models) {
      if (this.project?.models?.length > 0) {
        this.setModel(this.project?.models[0]);
      }
    }
  };

  setSelectedModelCategories = (selectedModelCategories: Record<string, ModelTreeNode>) => {
    this.selectedModelCategories = selectedModelCategories || {};
  };

  setExpandedKeys = (expandedKeys: any) => {
    //console.log("setExpandedKeys: ", expandedKeys);
    this.expandedKeys = expandedKeys || {};
  };

  setSelectedNodesKeys = (selectedNodesKeys: any) => {
    this.selectedNodesKeys = selectedNodesKeys || {};
  };

  setIsLoading = (isLoading: boolean) => {
    this.isPendingCount = Math.max(0, this.isPendingCount + (isLoading ? 1 : -1));
    this.isPending = this.isPendingCount > 0;
  };

  setColorMode = (colorMode: string) => {
    this.colorMode = <any>colorMode;
    localStorage.setItem("colorMode", this.colorMode);
  };

  setDefaultTheme = (theme: Theme) => {
    this.defaultTheme = theme;
  };

  toggleColorMode = () => {
    this.colorMode = this.colorMode === "light" ? "dark" : "light";
    localStorage.setItem("colorMode", this.colorMode);
  };

  setIsProjectStepperEnabled = (isProjectStepperEnabled: boolean) => {
    this.isProjectStepperEnabled = isProjectStepperEnabled;
    localStorage.setItem("isProjectStepperEnabled", this.isProjectStepperEnabled.toString());
  };

  toggleIsProjectStepperEnabled = () => {
    this.isProjectStepperEnabled = !this.isProjectStepperEnabled;
    localStorage.setItem("isProjectStepperEnabled", this.isProjectStepperEnabled.toString());
  };

  setIsLoggingEnabled = (isLoggingEnabled: boolean) => {
    this.isLoggingEnabled = isLoggingEnabled;
    localStorage.setItem("isLoggingEnabled", this.isLoggingEnabled.toString());

    if (!isLoggingEnabled) {
      if (!this.consoleLog) {
        this.consoleLog = console.log;
      }
      console.log = function () { };
    } else {
      if (this.consoleLog) {
        console.log = this.consoleLog;
      }
    }
  };

  setIsProfilingEnabled = (isProfilingEnabled: boolean) => {
    this.isProfilingEnabled = isProfilingEnabled;
    localStorage.setItem("isProfilingEnabled", this.isProfilingEnabled.toString());

    if (!isProfilingEnabled) {
    } else {
    }
  }

  toggleIsLoggingEnabled = () => {
    this.isLoggingEnabled = !this.isLoggingEnabled;
    localStorage.setItem("isLoggingEnabled", this.isLoggingEnabled.toString());
  };

  toggleIsProfilingEnabled = () => {
    this.isProfilingEnabled = !this.isProfilingEnabled;
    localStorage.setItem("isProfilingEnabled", this.isProfilingEnabled.toString());
  }

  toggleOpenResultsSettings = () => {
    this.openResultsSettings = !this.openResultsSettings;
  };

  setShowGrid = (showGrid: boolean) => {
    this.showGrid = showGrid;
    localStorage.setItem("showResultsGrid", this.showGrid.toString());
  };

  toggleShowGrid = () => {
    this.showGrid = !this.showGrid;
    localStorage.setItem("showResultsGrid", this.showGrid.toString());
  };

  setShowChart = (showChart: boolean) => {
    this.showChart = showChart;
    localStorage.setItem("showResultsChart", this.showChart.toString());
  };

  toggleShowChart = () => {
    this.showChart = !this.showChart;
    localStorage.setItem("showResultsChart", this.showChart.toString());
  };

  setShowSankey = (showSankey: boolean) => {
    this.showSankey = showSankey;
    localStorage.setItem("showResultsSankey", this.showSankey.toString());
  };

  toggleShowSankey = () => {
    this.showSankey = !this.showSankey;
    localStorage.setItem("showResultsSankey", this.showSankey.toString());
  };

  setSankeyOptions = (sankeyOptions: SankeyOptions) => {
    this.sankeyOptions = _.merge(this.sankeyOptions, sankeyOptions);
    localStorage.setItem("sankeyOptions", JSON.stringify(this.sankeyOptions));
  };

  toggleSankeyOption = (option: SankeyOptionKeys) => {
    this.sankeyOptions[option] = !this.sankeyOptions[option];
    this.sankeyOptions = _.merge({}, this.sankeyOptions);
    localStorage.setItem("sankeyOptions", JSON.stringify(this.sankeyOptions));
  };

  setLMViewer1 = (lmViewer: LmViewer3D | null) => {
    if (lmViewer === null) {
      this.lmViewer1?.destroy();
    }
    this.lmViewer1 = lmViewer || undefined;
  };

  setLMViewer2 = (lmViewer: LmViewer3D | null) => {
    if (lmViewer === null) {
      this.lmViewer2?.destroy();
    }
    this.lmViewer2 = lmViewer || undefined;
  };

  findLmViewer = (urn: string): LmViewer3D | undefined => {
    if (this.lmViewer1?.urns.includes(urn)) {
      return this.lmViewer1;
    }
    if (this.lmViewer2?.urns.includes(urn)) {
      return this.lmViewer2;
    }
  };

  getAdskThemeClass = () => {
    return this.colorMode === "dark" ? "dark-theme" : "light-theme";
  };

  getSelectedModels = (scenarioId: string): Model[] => {
    //identifier is either apsViewId or modelId, to support the non-viewer JSON models
    const selectedIdentifiers = this.selectionManager.getSelectedModelUrns(scenarioId);
    const models = this.project?.models?.filter((m: Model) => {
      const identifier = m.apsViewId || m.modelId;
      return identifier && selectedIdentifiers.includes(identifier);
    }) || [];
    return models;
  };

  setProjectTabBarSizes = (projectTabBarSizes: number[]) => {
    this.projectTabBarSizes = projectTabBarSizes;
    localStorage.setItem("projectTabBarSizes", JSON.stringify(this.projectTabBarSizes));
  };

  setMaterialSelectDialogSizes = (materialSelectDialogSizes: number[]) => {
    this.materialSelectDialogSizes = materialSelectDialogSizes;
    localStorage.setItem("materialSelectDialogSizes", JSON.stringify(this.materialSelectDialogSizes));
  };

  // TODO: this is capturing the scenarioName not scenarioId
  setCloneScenarioId = (scenarioName: string) => {
    this.scenarioName = scenarioName;
  };

  setCopyModelMaterial = (modelName: string) => {
    this.modelName = modelName;
  };

  resetRebarModelEdits = () => {
    this.rebarPercentEdits = {};
    this.rebarVolumeEdits = {};
    this.rebarCO2eEdits = {};
  };

  resetNonModelledModelEdits = () => {
    this.nonModelledPercentEdits = {};
    this.nonModelledVolumeEdits = {};
    this.nonModelledCO2eEdits = {};
  };
}

export default AppStore;
